import React, { useState } from "react";

import { useHistory } from "react-router-dom";

import { MenuItemListDto } from "services/menu/restModels";

import { localizedText } from "shared/lib/localizedMap";
import {
    BottomSheet,
    Button,
    ChevronDownIcon,
    EmptyState,
    IconButton,
    NotificationSnackbar,
    TuneIcon,
} from "shared/ui";

import { CategoryHeader } from "features/MenuEditor/components/CategoryHeader";
import { CategoryList } from "features/MenuEditor/components/CategoryList";
import { ItemActionsMenu } from "features/MenuEditor/components/ItemActionsMenu";
import { ItemsToolbar } from "features/MenuEditor/components/ItemsToolbar";
import { MenuEditorToolbar } from "features/MenuEditor/components/MenuEditorActions";
import { MenuEditorDialogs } from "features/MenuEditor/components/MenuEditorDialogs";
import {
    CategoryListSkeleton,
    MenuItemsGridSkeleton,
} from "features/MenuEditor/components/MenuEditorSkeleton";
import { MenuItemsGrid } from "features/MenuEditor/components/MenuItemsGrid";
import { MenuItemTile } from "features/MenuEditor/components/MenuItemTile";
import { MenuEditorContext } from "features/MenuEditor/context";
import { useMenuEditorState } from "features/MenuEditor/hooks/useMenuEditorState";

import {
    Aside,
    CategorySelectorButton,
    Container,
    Content,
    Message,
    MobileCategoryBar,
} from "./styles";

interface ActionsTarget<T> {
    target: T;
    anchorEl: HTMLElement;
}

const MenuEditorScreen = () => {
    const history = useHistory();
    const state = useMenuEditorState();

    const [categoryPickerOpen, setCategoryPickerOpen] = useState<boolean>(false);
    const [itemActions, setItemActions] = useState<ActionsTarget<MenuItemListDto> | null>(null);

    const { list } = state;

    const openCategorySettings = () => {
        if (!state.activeCategory) return;

        state.openDialog({ kind: "categorySettings", category: state.activeCategory });
    };

    const openItem = (item: MenuItemListDto) => {
        history.push(`/menus/regular/${state.menuId}/${item.menuCategoryId}/${item.id}`);
    };

    if (state.loading) {
        return (
            <Container>
                <Aside>
                    <CategoryListSkeleton />
                </Aside>
                <Content>
                    <MenuItemsGridSkeleton />
                </Content>
            </Container>
        );
    }

    if (state.error) {
        return <Message>Не удалось загрузить меню</Message>;
    }

    const renderItems = () => {
        if (list.loading) return <MenuItemsGridSkeleton />;

        if (list.error) return <Message>Не удалось загрузить позиции</Message>;

        if (list.items.length === 0) {
            const categoryName = state.activeCategory
                ? localizedText(state.activeCategory.name, state.locale)
                : "";
            const filtered = state.hasSearchQuery || state.filter !== "all";

            return (
                <EmptyState
                    title={
                        filtered
                            ? `В категории «${categoryName}» ничего не нашлось`
                            : `В категории «${categoryName}» пока пусто`
                    }
                    description={
                        filtered
                            ? "Поиск и фильтры работают внутри выбранной категории — попробуйте изменить условие или выбрать другую категорию."
                            : "Добавьте первую позицию — она появится в гостевом меню."
                    }
                    action={
                        filtered ? undefined : (
                            <Button
                                variant="contained"
                                onClick={() => state.openDialog({ kind: "createItem" })}
                            >
                                Добавить позицию
                            </Button>
                        )
                    }
                />
            );
        }

        return (
            <MenuItemsGrid
                hasMore={list.hasMore}
                loadingMore={list.loadingMore}
                loadedCount={list.items.length}
                onLoadMore={list.loadMore}
            >
                {list.items.map((item) => (
                    <MenuItemTile
                        key={item.id}
                        item={item}
                        locale={state.locale}
                        formatPrice={state.formatPrice}
                        toggling={state.togglingItemId === item.id}
                        onOpen={openItem}
                        onToggleStopList={state.toggleStopList}
                        onOpenActions={(target, anchorEl) =>
                            setItemActions({ target, anchorEl })
                        }
                    />
                ))}
            </MenuItemsGrid>
        );
    };

    const renderBody = () => {
        if (state.categories.length === 0) {
            return (
                <EmptyState
                    title="В меню пока нет категорий"
                    description="Категория — это раздел меню: завтраки, супы, напитки. Позиции добавляются внутрь категории."
                    action={
                        <Button
                            variant="contained"
                            onClick={() => state.openDialog({ kind: "createCategory" })}
                        >
                            Создать категорию
                        </Button>
                    }
                />
            );
        }

        return (
            <Container>
                <Aside>
                    <CategoryList
                        categories={state.visibleCategories}
                        totalCount={state.categories.length}
                        activeCategoryId={state.activeCategory?.id ?? null}
                        locale={state.locale}
                        query={state.categoriesQuery}
                        onQueryChange={state.setCategoriesQuery}
                        onSelect={state.selectCategory}
                        onOpenOrder={() => state.openDialog({ kind: "categoriesOrder" })}
                    />
                </Aside>

                <Content>
                    <MobileCategoryBar>
                        <CategorySelectorButton
                            type="button"
                            onClick={() => setCategoryPickerOpen(true)}
                        >
                            <span>
                                {state.activeCategory
                                    ? `${localizedText(state.activeCategory.name, state.locale)} · ${state.activeCategory.itemsCount}`
                                    : "Выберите категорию"}
                            </span>
                            <ChevronDownIcon />
                        </CategorySelectorButton>

                        <IconButton
                            label="Настройки категории"
                            size="large"
                            variant="outlined"
                            disabled={!state.activeCategory}
                            onClick={openCategorySettings}
                        >
                            <TuneIcon />
                        </IconButton>
                    </MobileCategoryBar>

                    <CategoryHeader
                        category={state.activeCategory}
                        locale={state.locale}
                        foundCount={state.hasSearchQuery ? list.total : null}
                        onOpenSettings={openCategorySettings}
                    />

                    <ItemsToolbar
                        query={state.itemsQuery}
                        onQueryChange={state.setItemsQuery}
                        filter={state.filter}
                        onFilterChange={state.setFilter}
                        counts={list.counts}
                    />

                    {renderItems()}
                </Content>
            </Container>
        );
    };

    return (
        <MenuEditorContext.Provider value={state}>
            <MenuEditorToolbar />

            {renderBody()}

            <BottomSheet
                open={categoryPickerOpen}
                title="Категории"
                onClose={() => setCategoryPickerOpen(false)}
            >
                <CategoryList
                    categories={state.visibleCategories}
                    totalCount={state.categories.length}
                    activeCategoryId={state.activeCategory?.id ?? null}
                    locale={state.locale}
                    query={state.categoriesQuery}
                    onQueryChange={state.setCategoriesQuery}
                    onSelect={(menuCategoryId) => {
                        state.selectCategory(menuCategoryId);
                        setCategoryPickerOpen(false);
                    }}
                />
            </BottomSheet>

            <ItemActionsMenu
                item={itemActions?.target ?? null}
                anchorEl={itemActions?.anchorEl ?? null}
                onClose={() => setItemActions(null)}
            />

            <MenuEditorDialogs />

            <NotificationSnackbar {...state.notify.snackbarProps} />
        </MenuEditorContext.Provider>
    );
};

export const MenuEditor = MenuEditorScreen;
